import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { Advice, ContentLayout10, mapReviewItemToAdvice } from '@components/content/components/content-layout-10/model';
import { LogsService } from '@services/logs';
import { ResponsiveService } from '@services/responsive';
import { ReviewsService } from '@services/reviews';
import { QuoteComponent } from '@shared/components/quote';
import { RateComponent } from '@shared/components/rate';
import { IconsDirective } from '@shared/directives';
import { map, tap } from 'rxjs/operators';
import { PageStore } from 'src/app/stores';

@Component({
  selector: 'app-content-layout-10',
  imports: [QuoteComponent, RateComponent, IconsDirective],
  templateUrl: './content-layout-10.component.html',
  styleUrl: './content-layout-10.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentLayout10Component {
  readonly listElement = viewChild.required<ElementRef<HTMLElement>>('listElement');
  readonly data = input.required<ContentLayout10>();
  breakpoints = inject(ResponsiveService).breakpoints;
  advices = signal<Advice[]>([]);
  currentScroll = signal(0);
  private readonly reviewsService = inject(ReviewsService);
  private readonly uuid = inject(PageStore).selectPath((state) => state.config.uuid);
  readonly #logsService = inject(LogsService);

  constructor() {
    effect((onCleanup) => {
      const storeUuid = this.uuid();
      if (!storeUuid) return;

      const sub = this.reviewsService
        .getReviewsByStore(storeUuid)
        .pipe(
          map((reviews) => reviews.items.map(mapReviewItemToAdvice)),
          tap((advices) => {
            this.advices.set(advices);
          })
        )
        .subscribe({
          error: (err: unknown) =>
            this.#logsService.error('ContentLayout10Component', 'Store reviews fetch failed', err),
        });
      onCleanup(() => sub.unsubscribe());
    });
  }

  onNext(length: number) {
    if (this.currentScroll() < this.listElement().nativeElement.clientWidth * (length - 1)) {
      this.currentScroll.set(this.currentScroll() + this.listElement().nativeElement.clientWidth);
    } else {
      this.currentScroll.set(0);
    }
    this.listElement().nativeElement.scrollTo({ left: this.currentScroll() });
  }

  onPrev(length: number) {
    if (this.currentScroll() > 0) {
      this.currentScroll.set(this.currentScroll() - this.listElement().nativeElement.clientWidth);
    } else {
      this.currentScroll.set(this.listElement().nativeElement.clientWidth * (length - 1));
    }
    this.listElement().nativeElement.scrollTo({ left: this.currentScroll() });
  }
}
